<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4 flex-1">
        <div class="w-[240px]">
          <SearchInput
            v-model="searchText"
            data-testid="biometry-search-input"
            label="Buscar"
            width="100%"
            hide-details
            density="compact"
            @input="handleSearch"
          />
        </div>
        <div class="flex items-center gap-2">
          <div class="relative" @click.stop="toggleFilter('status')">
            <SelectFilter
              data-testid="biometry-status-filter"
              label="Status"
              :is-open="openFilterName === 'status'"
              :options="statusOptions"
              :icon="statusIcon"
              :selected-value="selectedStatus"
              dropdown-width="w-64"
              @select="handleStatusSelect"
            />
          </div>
          <DateFilter
            ref="dateFilterRef"
            data-testid="biometry-date-filter"
            title="Período"
            :is-range="true"
            @select="handleDateSelect"
          />
          <div class="relative" @click.stop="toggleFilter('schedule')">
            <SelectFilter
              data-testid="biometry-schedule-filter"
              label="Escalas"
              :is-open="openFilterName === 'schedule'"
              :options="scheduleOptions"
              :icon="scheduleIcon"
              :selected-value="selectedSchedule"
              dropdown-width="w-64"
              searchable
              @select="handleScheduleSelect"
            />
          </div>
          <div class="relative" @click.stop="toggleFilter('unit')">
            <SelectFilter
              data-testid="biometry-unit-filter"
              label="Unidades"
              :is-open="openFilterName === 'unit'"
              :options="unitOptions"
              :icon="hospitalIcon"
              :selected-value="selectedUnit"
              dropdown-width="w-64"
              searchable
              @select="handleUnitSelect"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <p class="text-primary30 text-sm font-bold whitespace-nowrap">Ordenar por:</p>
        <div class="relative">
          <OutlineButton
            data-testid="biometry-sort-button"
            :text="currentSortLabel"
            width="auto"
            height="32px"
            :prepend-icon="mdiChevronDown"
            @click="isSortModalOpen = true"
          />
          <SortOptionsModal
            :is-open="isSortModalOpen"
            :options="sortModalOptions"
            :selected-value="sortOrder"
            @select="handleSortSelect"
            @update:is-open="isSortModalOpen = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue'
import { mdiChevronDown } from '@mdi/js'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import SortOptionsModal from '@/components/Filters/SortOptionsModal.vue'
import { BIOMETRY_STATUS_FILTER_OPTIONS } from '@/constants/facial-biometry.constants'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'

const statusIcon = defineAsyncComponent(() => import('@/assets/icons/status.vue'))
const scheduleIcon = defineAsyncComponent(() => import('@/assets/icons/scale.vue'))
const hospitalIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))

const emit = defineEmits<{
  search: [query: string]
  filterStatus: [value: string]
  filterDate: [payload: { start: Date | null; end: Date | null }]
  filterSchedule: [value: string]
  filterUnit: [value: string]
  sort: [order: string]
}>()

const scheduleStore = useScheduleStore()
const { notifyError } = useErrorToast()
const healthUnitStore = useHealthUnitStore()

const searchText = ref('')
const sortOrder = ref('ASC')
const isSortModalOpen = ref(false)
const openFilterName = ref<string | null>(null)
const dateFilterRef = ref<InstanceType<typeof DateFilter> | null>(null)
const selectedStatus = ref<string | null>(null)
const selectedSchedule = ref<string | null>(null)
const selectedUnit = ref<string | null>(null)

const statusOptions = BIOMETRY_STATUS_FILTER_OPTIONS.map((o) => ({
  label: o.name,
  value: o.id
}))

// `listSchedules` (GET /schedules) é a lista plana de escalas. `schedulesForPage`
// (GET /schedules/list) é a árvore empresa → unidade → escala, cujo nível de topo
// é a EMPRESA — este filtro mostrava nomes de empresa e filtrava por company.id.
const scheduleOptions = computed(() =>
  scheduleStore.listSchedules.map((s) => ({
    label: s.title,
    value: s.value
  }))
)

const unitOptions = computed(() =>
  (healthUnitStore.healthUnits?.data ?? []).map((u) => ({
    label: u.name,
    value: String(u.id)
  }))
)

const sortModalOptions = [
  { label: 'Mais antigo', value: 'ASC', direction: 'asc' as const, group: 'date' },
  { label: 'Mais recente', value: 'DESC', direction: 'desc' as const, group: 'date' }
]

const currentSortLabel = computed(
  () => sortModalOptions.find((o) => o.value === sortOrder.value)?.label ?? 'Mais antigo'
)

function handleClickOutside() {
  openFilterName.value = null
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  scheduleStore
    .getAllSchedules()
    .catch((error) => notifyError(error, 'Não foi possível carregar as escalas'))
  healthUnitStore.fetchHealthUnits()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  if (searchDebounce) clearTimeout(searchDebounce)
})

function toggleFilter(name: string) {
  if (name !== 'date') dateFilterRef.value?.close?.()
  openFilterName.value = openFilterName.value === name ? null : name
}

// Limpa o estado visual da barra sem emitir eventos — quem chama (Index) já
// reseta a store e refaz o fetch. Substitui o antigo remount por :key, que
// refazia as requests de escalas/unidades a cada "Limpar filtros".
function reset() {
  searchText.value = ''
  sortOrder.value = 'ASC'
  openFilterName.value = null
  selectedStatus.value = null
  selectedSchedule.value = null
  selectedUnit.value = null
  dateFilterRef.value?.reset?.()
}

defineExpose({ reset })

let searchDebounce: ReturnType<typeof setTimeout> | null = null

function handleSearch() {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => emit('search', searchText.value), 400)
}

function handleStatusSelect(value: string) {
  selectedStatus.value = value
  emit('filterStatus', value)
}

function handleDateSelect(payload: { start: Date | null; end: Date | null }) {
  emit('filterDate', payload)
}

function handleScheduleSelect(value: string) {
  selectedSchedule.value = value
  emit('filterSchedule', value)
}

function handleUnitSelect(value: string) {
  selectedUnit.value = value
  emit('filterUnit', value)
}

function handleSortSelect(value: string) {
  sortOrder.value = value
  isSortModalOpen.value = false
  emit('sort', value)
}
</script>
